// assets/evr-cart-refund-panel.js
// Custom element for the cart-drawer refund panel (<evr-cart-refund-panel>).
//
// Mirrors the ZPP API contract used by blocks/evr-insurance-check-v2.liquid:
//   GET {API_BASE}/insurers?preis=<capped>&kursId=<KURS_ID>  -> { insurers: [...] }
//   GET {API_BASE}/refund?preis=<capped>&kursId=<KURS_ID>&insurer=<name> -> { amount }
// The displayed effective price uses the FULL cart total; the refund is capped
// at data-refund-eligible-max (same logic as the PDP calculator).
//
// Morph handling: the cart drawer re-renders via the Section Rendering API on
// every add/qty change and idiomorph resets the panel DOM back to the server
// placeholders. So we re-sync every panel on the theme's `cart:update` event,
// on DOMContentLoaded, and once right now (for panels already in the DOM).
// Insurer + refund lookups are cached on window.EVR.zpp -> re-sync is instant.
//
// Debug: append ?crpdebug=1 to the URL to log lifecycle + fetch to the console.
(function () {
  window.EVR = window.EVR || {};
  window.EVR.zpp = window.EVR.zpp || {};

  var DEBUG = false;
  try { DEBUG = /[?&]crpdebug=1/.test(location.search); } catch (_) {}
  var dlog = function () {
    if (DEBUG) console.log.apply(console, ['[EVR-CRP]'].concat([].slice.call(arguments)));
  };

  var TOP_INSURERS = [
    'Techniker Krankenkasse',
    'BARMER',
    'DAK-Gesundheit',
    'AOK PLUS',
    'IKK classic',
  ];

  var norm = function (s) { return String(s == null ? '' : s).trim().toLowerCase(); };

  var eur0 = function (n) {
    if (!Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('de-DE', {
      style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
    }).format(n);
  };

  var insurerCache = window.EVR.zpp.__crpInsurers || (window.EVR.zpp.__crpInsurers = {});
  var refundCache = window.EVR.zpp.__crpRefunds || (window.EVR.zpp.__crpRefunds = {});

  function apiBase() {
    return (window.EVR && window.EVR.zpp && window.EVR.zpp.API_BASE) || '/apps/eversion-backend/api/zpp';
  }
  function kursId() {
    return (window.EVR && window.EVR.zpp && window.EVR.zpp.KURS_ID) || '';
  }

  async function getJson(path, params) {
    var qs = new URLSearchParams(params).toString();
    var res = await fetch(apiBase() + path + '?' + qs, { method: 'GET', credentials: 'same-origin' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    return res.json();
  }

  // Re-read data attributes and DOM refs, set the static total, (re)bind the
  // select, then load insurers + refund. Safe to call repeatedly (after morph).
  function syncPanel(el) {
    if (!el) return;
    // GDP referral visitors (?ref=gdp -> html.evr-ref-gdp) don't see the refund
    // panel (hidden via CSS); skip the ZPP calls entirely for them.
    if (document.documentElement.classList.contains('evr-ref-gdp')) return;
    var preis = parseFloat(el.dataset.preis) || 0;
    el.cartTotal = parseFloat(el.dataset.cartTotal) || preis;
    el.maxRefund = parseFloat(el.dataset.refundEligibleMax) || 150;
    el.preisForRefund = Math.min(preis, el.maxRefund);
    el.defaultInsurer = (el.dataset.defaultInsurer || 'Techniker Krankenkasse').trim();
    if (!el.selected) {
      el.selected = (el.dataset.selectedInsurer || '').trim() || el.defaultInsurer;
    }

    var select = el.querySelector('[data-crp-select]');
    el.select = select;
    el.refundEl = el.querySelector('[data-crp-refund]');
    el.insurerEl = el.querySelector('[data-crp-insurer]');
    el.effEl = el.querySelector('[data-crp-eff]');
    el.todayEl = el.querySelector('[data-crp-today]');

    // Equality-guarded write so it doesn't re-trigger the MutationObserver.
    var today = eur0(el.cartTotal);
    if (el.todayEl && el.todayEl.textContent !== today) el.todayEl.textContent = today;

    if (!select) return;

    // Skip if THIS select node was already wired+loaded. A morph/hydration that
    // swaps in a fresh server-rendered select clears the flag, so we re-run
    // after morphs and after the initial hydration (which reuses the panel node
    // and resets its children WITHOUT firing cart:update). Prevents loops: our
    // own DOM writes below land on a node that is now flagged ready.
    if (select.dataset.crpReady === '1') return;
    select.dataset.crpReady = '1';
    dlog('syncPanel: wiring select', { cartTotal: el.cartTotal, selected: el.selected });

    select.addEventListener('change', function () {
      var ins = select.value;
      if (!ins || ins === '__sep__') return;
      el.selected = ins;
      window.dispatchEvent(new CustomEvent('evr:zpp:selection', { detail: { insurer: ins } }));
      showFor(el, ins);
    });

    loadFor(el);
  }

  async function loadFor(el) {
    if (!kursId()) {
      // evr-zpp-config.js may not have set KURS_ID yet on first paint; retry once.
      dlog('no KURS_ID yet');
      if (!el.__crpRetry) {
        el.__crpRetry = true;
        setTimeout(function () { el.__crpRetry = false; loadFor(el); }, 400);
      }
      return;
    }
    try {
      var ck = el.preisForRefund + '|' + kursId();
      var list = insurerCache[ck];
      if (!list) {
        dlog('fetch /insurers', { preis: el.preisForRefund, kursId: kursId() });
        var data = await getJson('/insurers', { preis: String(el.preisForRefund), kursId: kursId() });
        list = Array.isArray(data && data.insurers) ? data.insurers : [];
        insurerCache[ck] = list;
      }
      dlog('insurers loaded', list.length);
      populate(el, list);
      await showFor(el, el.selected);
    } catch (e) {
      dlog('loadFor error', e && e.message);
      if (el.refundEl) el.refundEl.textContent = '—';
      if (el.effEl) el.effEl.textContent = eur0(el.cartTotal);
    }
  }

  function populate(el, list) {
    if (!el.select) return;
    var seen = new Set();
    var all = list
      .map(function (s) { return String(s == null ? '' : s).trim(); })
      .filter(Boolean)
      .filter(function (name) {
        var k = norm(name);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .sort(function (a, b) { return a.localeCompare(b, 'de', { sensitivity: 'base' }); });

    var byKey = new Map(all.map(function (n) { return [norm(n), n]; }));
    var top = TOP_INSURERS
      .map(function (t) { return byKey.get(norm(t)) || t; })
      .filter(Boolean);

    var ph = (el.select.querySelector('option[value=""]') || {}).textContent || 'Krankenkasse wählen…';
    el.select.innerHTML = '';

    var opt0 = document.createElement('option');
    opt0.value = '';
    opt0.textContent = ph;
    el.select.appendChild(opt0);

    top.forEach(function (name) {
      var o = document.createElement('option');
      o.value = name; o.textContent = name;
      el.select.appendChild(o);
    });

    if (all.length) {
      var sep = document.createElement('option');
      sep.value = '__sep__'; sep.textContent = '──────────'; sep.disabled = true;
      el.select.appendChild(sep);
      all.forEach(function (name) {
        var o = document.createElement('option');
        o.value = name; o.textContent = name;
        el.select.appendChild(o);
      });
    }

    var match = byKey.get(norm(el.selected));
    if (match) {
      el.select.value = match;
      el.selected = match;
    }
  }

  async function showFor(el, insurer) {
    if (!insurer) return;
    if (el.select && el.select.value !== insurer) {
      var has = Array.prototype.some.call(el.select.options, function (o) { return o.value === insurer; });
      if (has) el.select.value = insurer;
    }
    if (el.insurerEl) el.insurerEl.textContent = insurer;
    if (el.refundEl) el.refundEl.textContent = '…';
    if (el.effEl) el.effEl.textContent = '…';

    var refund;
    var rk = el.preisForRefund + '|' + kursId() + '|' + norm(insurer);
    try {
      if (rk in refundCache) {
        refund = refundCache[rk];
      } else {
        var data = await getJson('/refund', {
          preis: String(el.preisForRefund), kursId: kursId(), insurer: insurer,
        });
        refund = Number(data && data.amount);
        refundCache[rk] = refund;
      }
    } catch (e) {
      dlog('refund error', e && e.message);
      refund = NaN;
    }

    var eff = Number.isFinite(refund) ? Math.max(0, el.cartTotal - refund) : el.cartTotal;
    if (el.refundEl) el.refundEl.textContent = Number.isFinite(refund) ? '− ' + eur0(refund) : '—';
    if (el.effEl) el.effEl.textContent = eur0(eff);
    dlog('showFor', { insurer: insurer, refund: refund, eff: eff });
  }

  function runAll() {
    var panels = document.querySelectorAll('evr-cart-refund-panel');
    dlog('runAll, panels found:', panels.length);
    panels.forEach(syncPanel);
  }

  // --- Custom element: fires connectedCallback on initial render + morph inserts ---
  if (!(window.customElements && customElements.get('evr-cart-refund-panel'))) {
    class EvrCartRefundPanel extends HTMLElement {
      connectedCallback() { syncPanel(this); }
    }
    customElements.define('evr-cart-refund-panel', EvrCartRefundPanel);
    dlog('custom element defined');
  }

  // Coalesce many triggers into one run per frame.
  var __scheduled = false;
  function scheduleRunAll() {
    if (__scheduled) return;
    __scheduled = true;
    requestAnimationFrame(function () { __scheduled = false; runAll(); });
  }

  // --- One-time global bindings (script may load >1x; guard) ---
  if (!window.EVR.zpp.__crpCartBound) {
    window.EVR.zpp.__crpCartBound = true;

    // Re-sync after the cart drawer morphs.
    document.addEventListener('cart:update', scheduleRunAll);

    // The cart section re-renders on add/qty change AND hydrates on initial
    // load (e.g. opening the drawer after a reload with an item already in the
    // cart). idiomorph often reuses the panel node and resets its children —
    // clearing data-crp-ready — WITHOUT firing cart:update, so connectedCallback
    // never re-runs. A MutationObserver catches every such reset; scheduleRunAll
    // coalesces to one run per frame and syncPanel is a no-op for already-wired
    // selects, so there is no loop. Scoped to #header-group (contains the drawer)
    // to avoid reacting to unrelated page mutations.
    try {
      var moTarget = document.getElementById('header-group') || document.body;
      new MutationObserver(scheduleRunAll).observe(moTarget, { childList: true, subtree: true });
    } catch (_) {}

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleRunAll);
    }
    scheduleRunAll();

    // "So funktioniert die Erstattung" in the cart drawer should open the shared
    // how-to side sheet (blocks/evr-howto-refund-panel.liquid) instead of the
    // inline <details>. The sheet is a modal <dialog>; opened nested over the
    // (also modal) cart drawer it renders BEHIND it, so we close the drawer
    // first and reopen it once the sheet closes.
    //
    // The cart link also carries [data-evr-howto-trigger], so the panel block's
    // own global listener opens the sheet and preventDefault()s the <details>
    // toggle. On pages WITHOUT the sheet (no panel block) we don't intercept and
    // the inline <details> stays as a graceful fallback.
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-evr-crp-howto]');
      if (!link) return;
      var panel = document.querySelector('[data-evr-howto-panel]');
      if (!panel) return; // no sheet on this page -> inline <details> fallback
      var drawer = document.querySelector('.cart-drawer__dialog');
      if (drawer && drawer.open) {
        var closeBtn = drawer.querySelector('.cart-drawer__close-button');
        if (closeBtn) closeBtn.click();
        else { try { drawer.close(); } catch (_) {} }

        // Reopen the cart drawer as soon as the sheet STARTS closing (its
        // .is-open class is removed) instead of after the slide-out finishes
        // (the `close` event), so the two transitions overlap and feel smoother.
        // A MutationObserver catches every close path (button, backdrop, Esc).
        var reopenTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
        var sawOpen = false, reopened = false;
        var mo = new MutationObserver(function () {
          if (panel.classList.contains('is-open')) { sawOpen = true; return; }
          if (sawOpen && !reopened) {
            reopened = true;
            mo.disconnect();
            if (reopenTrigger) reopenTrigger.click();
          }
        });
        mo.observe(panel, { attributes: true, attributeFilter: ['class'] });
      }
    });
  }
})();
